import { useState, type JSX } from 'react';
import {
  CAMERA_TRANSPORTS,
  RECORDER_DEFAULT_MAX_CHANNELS,
  RECORDER_PROTOCOLS,
  type CameraTransport,
  type NetworkNode,
  type Recorder,
  type RecorderDetectResult,
  type RecorderProtocol,
} from '@netdeck/contracts';
import { formatMoment } from '../../shared/format/time';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { CollapsibleSection } from '../../shared/ui/CollapsibleSection';
import { Icon } from '../../shared/ui/Icon';
import { RecorderArchiveSettings } from '../archive/ArchiveSettings';
import { NodeCredentials } from '../vnc/NodeCredentials';
import { useCreateRecorder, useDetectChannels, useRecorders, useUpdateRecorder } from './api';

const PROTOCOL_LABEL_KEYS: Readonly<Record<RecorderProtocol, string>> = {
  dahua: 'video.recorder.protocol.dahua',
  hikvision: 'video.recorder.protocol.hikvision',
  rtsp: 'video.recorder.protocol.rtsp',
};

const TRANSPORT_LABEL_KEYS: Readonly<Record<CameraTransport, string>> = {
  tcp: 'video.transport.tcp',
  udp: 'video.transport.udp',
};

/**
 * Пресеты шаблонов для RTSP-регистратора.
 *
 * Регистраторы без открытого API часто отдают поток по путям Dahua или
 * Hikvision — прошивки у них заимствованные. Вписывать такой путь руками
 * значит однажды ошибиться в `subtype`, и ошибка проявится только как
 * «канал без видео».
 */
const TEMPLATE_PRESETS: Readonly<Record<'dahua' | 'hikvision', { main: string; sub: string }>> = {
  dahua: {
    main: '/cam/realmonitor?channel={channel}&subtype=0',
    sub: '/cam/realmonitor?channel={channel}&subtype=1',
  },
  hikvision: { main: '/Streaming/Channels/{channel}01', sub: '/Streaming/Channels/{channel}02' },
};

/** Что сказать, когда учётных данных RTSP ещё не завели. */
const NO_RTSP_CREDENTIALS = 'credentials.noRtspHint';

/**
 * Видеорегистратор в панели узла (ADR 037).
 *
 * Устроен как раздел камеры и по той же причине: регистратор — свойство
 * узла, и всё про него — способ разговора, каналы, архив, пароль — должно
 * быть в одном месте рядом с адресом и мониторами.
 */
export function RecorderSection({
  node,
  open,
  onToggle,
  canView,
  canWrite,
  canArchive,
  onOpen,
  onOpenArchive,
}: {
  node: NetworkNode;
  /** Раскрыт ли раздел. Решает это панель: раскрытым может быть только один. */
  open: boolean;
  onToggle: () => void;
  canView: boolean;
  canWrite: boolean;
  canArchive: boolean;
  onOpen: (cameraId: string) => void;
  onOpenArchive: (cameraId: string) => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  const recorders = useRecorders(canView && node.kind === 'nvr');
  const recorder = recorders.data?.find((item) => item.nodeId === node.id) ?? null;

  if (node.kind !== 'nvr') {
    return null;
  }

  // На стену и в архив — первый канал, отдававший видео: пустой первый вход
  // встречал бы переход чёрной ячейкой.
  const first =
    recorder?.channels.find((channel) => channel.available !== false) ??
    recorder?.channels[0] ??
    null;

  return (
    <CollapsibleSection
      title={t('video.recorder.title')}
      icon="nodeNvr"
      /* Регистратора ещё нет — сворачивать нечего: внутри одна форма
         создания, и за шевроном она была бы спрятана вместе с единственным
         способом регистратор завести. */
      open={recorder === null ? null : open}
      onToggle={onToggle}
      actions={
        recorder === null || first === null ? null : (
          <div className="flex items-center gap-1.5">
            {canArchive && recorder.archiveProvider !== null ? (
              <Button
                size="sm"
                variant="secondary"
                icon="layers"
                disabled={!recorder.isEnabled}
                onClick={() => onOpenArchive(first.cameraId)}
              >
                {t('nav.archive')}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="primary"
              icon="expand"
              disabled={!canView || !recorder.isEnabled}
              onClick={() => onOpen(first.cameraId)}
            >
              {t('video.toWall')}
            </Button>
          </div>
        )
      }
    >
      {recorder === null ? (
        <NewRecorder
          key={node.id}
          node={node}
          canWrite={canWrite}
          // Заведённый регистратор тут же показывает свои настройки: до
          // создания раздел был несворачиваемым, и закрыться на ровном месте
          // он выглядел бы как потеря только что набранного.
          onCreated={() => {
            if (!open) {
              onToggle();
            }
          }}
        />
      ) : (
        <>
          <ChannelsSummary recorder={recorder} canWrite={canWrite} />
          {/* Ключ по регистратору: формы набирают поля один раз, при появлении. */}
          <div key={recorder.id} className="flex flex-col gap-2.5">
            <RecorderForm recorder={recorder} canWrite={canWrite} />
            <RecorderArchiveSettings recorder={recorder} canWrite={canWrite} />
            <NodeCredentials nodeId={node.id} service="rtsp" emptyHint={NO_RTSP_CREDENTIALS} />
          </div>
        </>
      )}
    </CollapsibleSection>
  );
}

/**
 * Каналы, последнее определение и кнопка определить заново.
 *
 * Каналы называются числом, а не списком. Список из шестнадцати строк
 * занимал полпанели и не отвечал ни на один вопрос: имена у каналов
 * регистратора одинаковые, а выбирают канал не здесь, а на стене и в архиве.
 * Числом сказано то единственное, что от этого раздела нужно, — сколько
 * каналов платформа нашла и сколько из них молчит.
 */
function ChannelsSummary({
  recorder,
  canWrite,
}: {
  recorder: Recorder;
  canWrite: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();
  const errorText = useErrorText();

  const detect = useDetectChannels();
  const [result, setResult] = useState<RecorderDetectResult | null>(null);

  // `null` — канал ещё не проверялся; молчащим он от этого не становится.
  const silent = recorder.channels.filter((channel) => channel.available === false).length;

  return (
    <div className="flex flex-col gap-1.5">
      <dl className="grid grid-cols-[86px_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        <dt className="text-dim">{t('video.recorder.protocolLabel')}</dt>
        <dd>{t(PROTOCOL_LABEL_KEYS[recorder.protocol])}</dd>

        <dt className="text-dim">{t('video.recorder.channels')}</dt>
        <dd className="flex flex-wrap items-center gap-x-1.5">
          {recorder.channels.length === 0 ? (
            <span className="text-dim">{t('video.recorder.noChannels')}</span>
          ) : (
            <>
              <span>
                {t('video.recorder.channelsDetected', { count: recorder.channels.length })}
              </span>
              {/* Молчащие каналы — знаком и словом, не одним цветом (§12). */}
              {silent === 0 ? null : (
                <span className="flex items-center gap-1 text-dim">
                  <Icon name="cameraOff" />
                  {t('video.recorder.withoutVideo', { silent })}
                </span>
              )}
            </>
          )}
        </dd>

        <dt className="text-dim">{t('video.recorder.lastDetect')}</dt>
        <dd className={recorder.lastDetectError === null ? 'font-mono' : 'text-down-fg'}>
          {recorder.lastDetectAt === null
            ? t('video.recorder.neverDetected')
            : recorder.lastDetectError === null
              ? formatMoment(recorder.lastDetectAt, language)
              : messageText(recorder.lastDetectError, t)}
        </dd>
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        {canWrite ? (
          <Button
            size="sm"
            variant="ghost"
            icon="refresh"
            disabled={detect.isPending}
            onClick={() => detect.mutate(recorder.id, { onSuccess: setResult })}
          >
            {detect.isPending ? t('video.recorder.detecting') : t('video.recorder.detect')}
          </Button>
        ) : null}
        {recorder.hasCredential ? null : (
          <span className="text-[11px] text-dim">{t('video.recorder.noCredential')}</span>
        )}
      </div>

      {detect.error === null ? null : (
        <p className="text-[11px] text-down-fg">{errorText(detect.error)}</p>
      )}
      {result === null || result.error !== null ? null : (
        <p className="text-[11px] text-muted">
          {t('video.recorder.detected', {
            found: result.found,
            added: result.added,
            removed: result.removed,
          })}
        </p>
      )}
    </div>
  );
}

/** Поля шаблонов RTSP с пресетами. Общие у заведения и правки. */
function TemplateFields({
  idPrefix,
  mainTemplate,
  subTemplate,
  disabled,
  onChange,
}: {
  idPrefix: string;
  mainTemplate: string;
  subTemplate: string;
  disabled: boolean;
  onChange: (main: string, sub: string) => void;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <label className="text-dim" htmlFor={`rec-main-${idPrefix}`}>
        {t('video.recorder.mainTemplate')}
      </label>
      <div className="flex flex-col gap-1">
        <input
          id={`rec-main-${idPrefix}`}
          value={mainTemplate}
          disabled={disabled}
          placeholder="/live/ch{channel}/main"
          onChange={(event) => onChange(event.target.value, subTemplate)}
          className="h-7 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
        />
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-dim">
          <span>{t('video.recorder.preset')}</span>
          {(['dahua', 'hikvision'] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={disabled}
              className="rounded-xs border border-line px-1.5 hover:text-fg"
              onClick={() => onChange(TEMPLATE_PRESETS[preset].main, TEMPLATE_PRESETS[preset].sub)}
            >
              {t(`video.vendor.${preset}`)}
            </button>
          ))}
        </div>
      </div>

      <label className="text-dim" htmlFor={`rec-sub-${idPrefix}`}>
        {t('video.recorder.subTemplate')}
      </label>
      <div className="flex flex-col gap-1">
        <input
          id={`rec-sub-${idPrefix}`}
          value={subTemplate}
          disabled={disabled}
          placeholder={t('video.notSet')}
          onChange={(event) => onChange(mainTemplate, event.target.value)}
          className="h-7 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
        />
        <p className="text-[11px] leading-snug text-dim">{t('video.recorder.templateHint')}</p>
      </div>
    </>
  );
}

/** Заведение регистратора на узле, у которого его ещё нет. */
function NewRecorder({
  node,
  canWrite,
  onCreated,
}: {
  node: NetworkNode;
  canWrite: boolean;
  onCreated: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const create = useCreateRecorder();
  const detect = useDetectChannels();

  const [protocol, setProtocol] = useState<RecorderProtocol>('dahua');
  const [httpPort, setHttpPort] = useState('80');
  const [mainTemplate, setMainTemplate] = useState('');
  const [subTemplate, setSubTemplate] = useState('');

  if (!canWrite) {
    return <p className="text-xs text-dim">{t('video.recorder.forbidden')}</p>;
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            nodeId: node.id,
            protocol,
            httpPort: Number(httpPort) || 80,
            ...(protocol === 'rtsp'
              ? {
                  mainTemplate: mainTemplate.trim(),
                  subTemplate: subTemplate.trim().length === 0 ? null : subTemplate.trim(),
                }
              : {}),
          },
          {
            // Каналы определяются сразу: регистратор без каналов на стену
            // не положить, и вторая кнопка ради очевидного шага лишняя.
            onSuccess: (created) => {
              detect.mutate(created.id);
              onCreated();
            },
          },
        );
      }}
    >
      <p className="text-xs leading-snug text-muted">{t('video.recorder.newHint')}</p>

      <div className="grid grid-cols-[86px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 text-sm">
        <label className="text-dim" htmlFor={`rec-new-protocol-${node.id}`}>
          {t('video.recorder.protocolLabel')}
        </label>
        <select
          id={`rec-new-protocol-${node.id}`}
          value={protocol}
          onChange={(event) => setProtocol(event.target.value as RecorderProtocol)}
          className="h-7 w-full min-w-0 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
        >
          {RECORDER_PROTOCOLS.map((value) => (
            <option key={value} value={value}>
              {t(PROTOCOL_LABEL_KEYS[value])}
            </option>
          ))}
        </select>

        {protocol === 'rtsp' ? (
          <TemplateFields
            idPrefix={`new-${node.id}`}
            mainTemplate={mainTemplate}
            subTemplate={subTemplate}
            disabled={false}
            onChange={(main, sub) => {
              setMainTemplate(main);
              setSubTemplate(sub);
            }}
          />
        ) : (
          <>
            <label className="text-dim" htmlFor={`rec-new-port-${node.id}`}>
              {t('video.recorder.httpPort')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id={`rec-new-port-${node.id}`}
                value={httpPort}
                inputMode="numeric"
                onChange={(event) => setHttpPort(event.target.value)}
                className="h-7 w-20 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
              />
              <span className="text-[11px] text-dim">{t('video.recorder.httpPortHint')}</span>
            </div>
          </>
        )}
      </div>

      {create.error === null ? null : (
        <p className="text-[11px] text-down-fg">{errorText(create.error)}</p>
      )}

      <Button size="sm" variant="secondary" icon="plus" type="submit" disabled={create.isPending}>
        {t('video.recorder.create')}
      </Button>
    </form>
  );
}

/** Параметры заведённого регистратора. */
function RecorderForm({
  recorder,
  canWrite,
}: {
  recorder: Recorder;
  canWrite: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const update = useUpdateRecorder(recorder.id);

  const [protocol, setProtocol] = useState<RecorderProtocol>(recorder.protocol);
  const [httpPort, setHttpPort] = useState(String(recorder.httpPort));
  const [scheme, setScheme] = useState(recorder.scheme);
  const [rtspPort, setRtspPort] = useState(String(recorder.rtspPort));
  const [transport, setTransport] = useState<CameraTransport>(recorder.transport);
  const [mainTemplate, setMainTemplate] = useState(recorder.mainTemplate ?? '');
  const [subTemplate, setSubTemplate] = useState(recorder.subTemplate ?? '');
  const [maxChannels, setMaxChannels] = useState(String(recorder.maxChannels));
  const [isEnabled, setIsEnabled] = useState(recorder.isEnabled);

  return (
    <form
      className="grid grid-cols-[86px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate({
          protocol,
          httpPort: Number(httpPort) || 80,
          scheme,
          rtspPort: Number(rtspPort) || 554,
          transport,
          mainTemplate: mainTemplate.trim().length === 0 ? null : mainTemplate.trim(),
          subTemplate: subTemplate.trim().length === 0 ? null : subTemplate.trim(),
          maxChannels: Number(maxChannels) || RECORDER_DEFAULT_MAX_CHANNELS,
          isEnabled,
        });
      }}
    >
      <label className="text-dim" htmlFor={`rec-protocol-${recorder.id}`}>
        {t('video.recorder.protocolLabel')}
      </label>
      <select
        id={`rec-protocol-${recorder.id}`}
        value={protocol}
        disabled={!canWrite}
        onChange={(event) => setProtocol(event.target.value as RecorderProtocol)}
        className="h-7 w-full min-w-0 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
      >
        {RECORDER_PROTOCOLS.map((value) => (
          <option key={value} value={value}>
            {t(PROTOCOL_LABEL_KEYS[value])}
          </option>
        ))}
      </select>

      {protocol === 'rtsp' ? (
        <TemplateFields
          idPrefix={recorder.id}
          mainTemplate={mainTemplate}
          subTemplate={subTemplate}
          disabled={!canWrite}
          onChange={(main, sub) => {
            setMainTemplate(main);
            setSubTemplate(sub);
          }}
        />
      ) : (
        <>
          <label className="text-dim" htmlFor={`rec-http-${recorder.id}`}>
            {t('video.recorder.httpPort')}
          </label>
          <div className="flex items-center gap-2">
            <input
              id={`rec-http-${recorder.id}`}
              value={httpPort}
              disabled={!canWrite}
              inputMode="numeric"
              onChange={(event) => setHttpPort(event.target.value)}
              className="h-7 w-20 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
            />
            <select
              value={scheme}
              disabled={!canWrite}
              aria-label={t('archive.settings.schemeAria')}
              onChange={(event) => setScheme(event.target.value as 'http' | 'https')}
              className="h-7 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
            >
              <option value="http">http</option>
              <option value="https">https</option>
            </select>
          </div>
        </>
      )}

      <label className="text-dim" htmlFor={`rec-rtsp-${recorder.id}`}>
        {t('video.recorder.rtspPort')}
      </label>
      <input
        id={`rec-rtsp-${recorder.id}`}
        value={rtspPort}
        disabled={!canWrite}
        inputMode="numeric"
        onChange={(event) => setRtspPort(event.target.value)}
        className="h-7 w-24 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
      />

      <label className="text-dim" htmlFor={`rec-max-${recorder.id}`}>
        {t('video.recorder.maxChannels')}
      </label>
      <input
        id={`rec-max-${recorder.id}`}
        value={maxChannels}
        disabled={!canWrite}
        inputMode="numeric"
        onChange={(event) => setMaxChannels(event.target.value)}
        className="h-7 w-24 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
      />

      <label className="text-dim" htmlFor={`rec-transport-${recorder.id}`}>
        {t('video.transportLabel')}
      </label>
      <select
        id={`rec-transport-${recorder.id}`}
        value={transport}
        disabled={!canWrite}
        onChange={(event) => setTransport(event.target.value as CameraTransport)}
        className="h-7 w-full min-w-0 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
      >
        {CAMERA_TRANSPORTS.map((value) => (
          <option key={value} value={value}>
            {t(TRANSPORT_LABEL_KEYS[value])}
          </option>
        ))}
      </select>

      <span className="text-dim">{t('video.enabled')}</span>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={isEnabled}
          disabled={!canWrite}
          onChange={(event) => setIsEnabled(event.target.checked)}
        />
        {t('video.recorder.enabledHint')}
      </label>

      {update.error === null ? null : (
        <p className="col-span-2 text-[11px] text-down-fg">{errorText(update.error)}</p>
      )}

      {canWrite ? (
        <Button
          size="sm"
          variant="secondary"
          icon="check"
          type="submit"
          className="col-start-2"
          disabled={update.isPending}
        >
          {t('common.action.save')}
        </Button>
      ) : null}
    </form>
  );
}
